// mindmetal-app/web/src/pages/employee/LogViewer.tsx
import { useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, Search, RefreshCw, Download, AlertCircle, AlertTriangle, Info, Bug } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Select } from '@personic/shared'
import { useLogs, type LogEntry } from '@/hooks/useDevTools'

const LOG_LEVELS = [
  { value: 'all', label: 'All Levels' },
  { value: 'ERROR', label: 'Error' },
  { value: 'WARN', label: 'Warning' },
  { value: 'INFO', label: 'Info' },
  { value: 'DEBUG', label: 'Debug' },
]

const TIME_RANGES = [
  { value: '', label: 'All Time' },
  { value: '1h', label: 'Last Hour' },
  { value: '6h', label: 'Last 6 Hours' },
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
]

const levelConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  ERROR: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50' },
  WARN: { icon: AlertTriangle, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  INFO: { icon: Info, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  DEBUG: { icon: Bug, color: 'text-warm-500', bgColor: 'bg-warm-50' },
}

export default function LogViewer() {
  const [level, setLevel] = useState('all')
  const [search, setSearch] = useState('')
  const [timeRange, setTimeRange] = useState('')
  const [page, setPage] = useState(1)
  const [expandedLog, setExpandedLog] = useState<string | null>(null)

  const getTimeParams = () => {
    if (!timeRange) return {}
    const now = new Date()
    const start = new Date(now)

    switch (timeRange) {
      case '1h':
        start.setHours(start.getHours() - 1)
        break
      case '6h':
        start.setHours(start.getHours() - 6)
        break
      case '24h':
        start.setDate(start.getDate() - 1)
        break
      case '7d':
        start.setDate(start.getDate() - 7)
        break
    }

    return { startTime: start.toISOString(), endTime: now.toISOString() }
  }

  const { data, isLoading, refetch, isFetching } = useLogs({
    level: level !== 'all' ? level : undefined,
    search: search || undefined,
    ...getTimeParams(),
    page,
    limit: 50,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    refetch()
  }

  const exportLogs = () => {
    if (!data?.logs) return

    const content = data.logs
      .map(
        (log) =>
          `[${log.timestamp}] [${log.level}] [${log.source}] ${log.message}${
            log.metadata ? ' ' + JSON.stringify(log.metadata) : ''
          }`
      )
      .join('\n')

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `logs-${new Date().toISOString().slice(0, 10)}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const LogRow = ({ log }: { log: LogEntry }) => {
    const config = levelConfig[log.level] || levelConfig.INFO
    const Icon = config.icon
    const isExpanded = expandedLog === log.id

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="border-b border-warm-100 last:border-b-0"
      >
        <button
          onClick={() => setExpandedLog(isExpanded ? null : log.id)}
          className="w-full text-left px-4 py-3 hover:bg-warm-50 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className={`p-1 rounded ${config.bgColor}`}>
              <Icon className={`w-4 h-4 ${config.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                  {log.level}
                </span>
                <span className="text-xs text-warm-400 font-mono">{log.source}</span>
                <span className="text-xs text-warm-300">|</span>
                <span className="text-xs text-warm-400">{formatTimestamp(log.timestamp)}</span>
              </div>
              <p className="text-sm text-midnight-800 font-mono truncate">{log.message}</p>
            </div>
          </div>
        </button>

        {isExpanded && log.metadata && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-3"
          >
            <div className="ml-8 p-3 bg-midnight-900 rounded-lg">
              <p className="text-xs text-warm-400 mb-1">Metadata:</p>
              <pre className="text-sm text-warm-100 font-mono overflow-x-auto">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          </motion.div>
        )}
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold text-midnight-900">Log Viewer</h1>
          <p className="text-warm-500">Browse and search system logs</p>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Input
                  label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search logs..."
                  leftIcon={<Search className="w-4 h-4" />}
                />
              </div>
              <div className="w-36">
                <Select
                  label="Level"
                  options={LOG_LEVELS}
                  value={level}
                  onChange={(e) => {
                    setLevel(e.target.value)
                    setPage(1)
                  }}
                />
              </div>
              <div className="w-44">
                <Select
                  label="Time Range"
                  options={TIME_RANGES}
                  value={timeRange}
                  onChange={(e) => {
                    setTimeRange(e.target.value)
                    setPage(1)
                  }}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" isLoading={isFetching}>
                  Apply
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                </Button>
                <Button type="button" variant="outline" onClick={exportLogs}>
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>

      {/* Logs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card padding="none">
          <CardHeader className="border-b border-warm-200">
            <CardTitle className="flex items-center justify-between">
              <span>Logs</span>
              <span className="text-sm font-normal text-warm-500">
                {data?.total || 0} entries
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
              </div>
            ) : data?.logs.length === 0 ? (
              <div className="text-center py-12 text-warm-500">
                No logs found matching your criteria
              </div>
            ) : (
              <div className="divide-y divide-warm-100">
                {data?.logs.map((log) => (
                  <LogRow key={log.id} log={log} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Pagination */}
      {data && data.total > 50 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-warm-500">
            Page {page} of {Math.ceil(data.total / 50)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.ceil(data.total / 50)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
